import { gql, useMutation } from '@apollo/client';
import * as React from 'react';
import { useState } from 'react';

import Form from 'components/lib/form/Form';
import CheckAnimation from 'components/lib/ui/CheckAnimation';
import ConfirmationSuccess from 'components/lib/ui/ConfirmationSuccess';
import FlexContainer from 'components/lib/ui/FlexContainer';
import { HeaderText } from 'components/plan/PlanGrid';
import { DeleteMemberModalInnerForm } from 'components/settings/members/DeleteMemberModalInnerForm';

import useToast from 'lib/hooks/useToast';

import { MEMBERS } from 'common/constants/copy';

import type {
  DeleteUserFromHousehold,
  DeleteUserFromHouseholdVariables,
} from 'common/generated/graphQlTypes/DeleteUserFromHousehold';

export type DeleteMemberFormValues = {
  memberNameConfirmation: string;
};

type Props = {
  memberInfo: { name: string; id: string };
  onClose: () => void;
  onDeleteSuccess: () => void;
};

export const DeleteMemberModal: React.FC<Props> = ({
  memberInfo: { id, name },
  onClose,
  onDeleteSuccess,
}) => {
  const { openErrorToast } = useToast();

  const [deleteUserFromHousehold, { loading: deleteUserFromHouseholdLoading }] = useMutation<
    DeleteUserFromHousehold,
    DeleteUserFromHouseholdVariables
  >(DELETE_USER_FROM_HOUSEHOLD);

  const [hasCompleted, setHasCompleted] = useState(false);

  const handleSubmit = (values: DeleteMemberFormValues) => {
    deleteUserFromHousehold({
      variables: { input: { userId: id } },
      onError: () => {
        openErrorToast({
          description: MEMBERS.REMOVE_MEMBER.ERROR_MESSAGE,
        });
      },
      onCompleted: (data) => {
        if (!data.deleteUserFromHousehold?.deleted) {
          openErrorToast({
            description: MEMBERS.REMOVE_MEMBER.ERROR_MESSAGE,
          });
          return;
        }

        setHasCompleted(true);
        onDeleteSuccess();
      },
    });
  };

  if (hasCompleted) {
    return (
      <ConfirmationSuccess title="" dismissText="Done" onDismiss={onClose}>
        <FlexContainer column gap="large" full alignCenter justifyCenter>
          <CheckAnimation size={80} />
          <HeaderText>{`${name} was removed from your household`}</HeaderText>
        </FlexContainer>
      </ConfirmationSuccess>
    );
  }

  return (
    <Form
      initialValues={{
        memberNameConfirmation: '',
      }}
      onSubmit={handleSubmit}
      isInitialValid={false}
    >
      <DeleteMemberModalInnerForm
        name={name}
        onCancel={onClose}
        disabled={deleteUserFromHouseholdLoading}
      />
    </Form>
  );
};

const DELETE_USER_FROM_HOUSEHOLD = gql`
  mutation Web_DeleteUserFromHousehold($input: DeleteUserFromHouseholdMutationInput!) {
    deleteUserFromHousehold(input: $input) {
      deleted
      errors {
        message
      }
    }
  }
`;
